"use client"

import { useEffect, useId, useMemo, useState, type ReactNode } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { CalculatorBookingCta } from "@/components/tools/calculator-booking-cta"
import { buttonVariants } from "@/components/ui/button"
import { fib4CalculatorKeys as k } from "@/i18n/fib4-calculator-keys"
import { formatDecimal } from "@/lib/locale-number-format"
import { track } from "@/lib/analytics"
import { cn } from "@/lib/utils"
import {
  FIB4_BANDS,
  FIB4_MAX_AGE,
  FIB4_MIN_AGE,
  MAX_ALT,
  MAX_AST,
  MAX_PLATELETS,
  MIN_ALT,
  MIN_AST,
  MIN_PLATELETS,
  fib4AgeGroupFor,
  fib4BandFor,
  fib4HasReducedAccuracy,
  fib4Problem,
  fib4Score,
  roundScore,
  type Fib4AgeGroupDef,
  type Fib4Band,
  type Fib4Problem,
} from "@/lib/fib4"

/**
 * The interactive part of /tools/fib4-calculator.
 *
 * One centred column: the page intro, then the form, then the result panel,
 * which does not exist until Calculate has run. One component owns all three
 * because the form and the result share state; the `intro` slot is what keeps
 * the h1 in the client file, where the page's structure is meant to stay
 * readable.
 *
 * Deliberately framer-motion-free: the widget is always mounted, so any
 * animation here would have to clear the `<AnimatePresence>` + cacheComponents
 * trap (see `/animation-cache-trap`). CSS transitions cover what it needs.
 *
 * Nothing is submitted, stored or tracked. That matters here for the same
 * reason it does on the SCORE2 page: the four inputs are the contents of a lab
 * report, and the arithmetic happens in the browser and never leaves it.
 */

/**
 * The score axis drawn under the result: 0 to 5, which puts both age groups'
 * cut-offs (1.30 / 2.00 and 2.67) comfortably inside it with room to see how
 * far past one a result sits. Above 5 the marker clamps to the right edge.
 */
const SCALE_MAX = 5

/** Exported for the reference table's band chips in the client file. */
export const BAND_STYLES: Record<
  Fib4Band,
  { bar: string; chip: string; accent: string }
> = {
  low: {
    bar: "bg-deep-green-400",
    chip: "bg-deep-green-100 text-deep-green-800",
    accent: "text-deep-green-600",
  },
  indeterminate: {
    bar: "bg-terracotta-300",
    chip: "bg-terracotta-100 text-terracotta-900",
    accent: "text-terracotta-600",
  },
  high: {
    bar: "bg-terracotta-600",
    chip: "bg-terracotta-200 text-terracotta-950",
    accent: "text-terracotta-800",
  },
}

const inputClasses =
  "type-body-lg w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-neutral-900 tabular-nums focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-aubergine-400 focus-visible:outline-none"

const unitSuffixClasses =
  "type-body-md pointer-events-none absolute inset-y-0 right-4 flex items-center text-neutral-500"

const labelClasses = "type-label-md block text-neutral-700 normal-case"

const hintClasses = "type-body-sm mt-2 text-neutral-500"

/**
 * NaN for a blank or unparseable field. `Number("")` is 0, which would read a
 * still-empty box as a real zero: an empty platelet field would submit as
 * 0 G/L, and dividing by it returns Infinity rather than anything the
 * plausibility check would catch as a typo.
 */
function parseField(value: string): number {
  const trimmed = value.trim().replace(",", ".")
  if (trimmed === "") return NaN
  return Number(trimmed)
}

interface Fib4Result {
  /** Rounded to the two decimals the reader sees, see `roundScore`. */
  score: number
  band: Fib4Band
  ageGroup: Fib4AgeGroupDef
  /** Age below which a low score proves much less. See `lib/fib4.ts`. */
  reducedAccuracy: boolean
}

export function Fib4CalculatorWidget({ intro }: { intro?: ReactNode }) {
  const t = useTranslations("fib4Calculator")
  // The score is rendered here rather than coming from the catalog, so the
  // decimal separator has to be chosen explicitly: a French page printing
  // "1.42" beside prose that says "1,42" contradicts itself.
  const locale = useLocale()
  const fieldId = useId()
  /**
   * Flipped in an effect, and stamped onto the root as `data-hydrated`.
   *
   * Not decoration: the inputs are controlled, so anything typed into them
   * before React owns them is silently discarded, and an e2e test has no other
   * way to know when that moment has passed. On the BMI page a blind retry
   * loop kept flaking on a loaded CI runner until this signal existed. One
   * boolean, renders nothing.
   */
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  const [age, setAge] = useState("")
  const [ast, setAst] = useState("")
  const [alt, setAlt] = useState("")
  const [platelets, setPlatelets] = useState("")

  /**
   * The result only exists after Calculate, and any change to any field drops
   * it. A stale figure sitting beside edited inputs is the one thing a
   * side-by-side layout must not show, and it matters more here than on a BMI
   * page: this number is the one a reader might take to an appointment.
   */
  const [result, setResult] = useState<Fib4Result | null>(null)
  const [problem, setProblem] = useState<Fib4Problem | null>(null)

  /** Every field setter goes through this. */
  function invalidate() {
    setResult(null)
    setProblem(null)
  }

  const hasInput =
    age.trim() !== "" ||
    ast.trim() !== "" ||
    alt.trim() !== "" ||
    platelets.trim() !== ""

  /** The four inputs as numbers, or null while any field is blank. */
  const measurements = useMemo(() => {
    const years = parseField(age)
    const astValue = parseField(ast)
    const altValue = parseField(alt)
    const plateletValue = parseField(platelets)
    if (
      !Number.isFinite(years) ||
      !Number.isFinite(astValue) ||
      !Number.isFinite(altValue) ||
      !Number.isFinite(plateletValue)
    ) {
      return null
    }
    return {
      age: years,
      ast: astValue,
      alt: altValue,
      platelets: plateletValue,
    }
  }, [age, ast, alt, platelets])

  /**
   * Plausibility is checked here rather than by disabling the button: a
   * disabled control with no explanation leaves the reader guessing, so the
   * button enables as soon as every field is filled and an unscoreable entry
   * gets the message that explains which of the three things went wrong.
   *
   * The score is rounded first and banded from that same rounded value (see
   * `roundScore`) so the label always describes the number on screen.
   */
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!measurements) return
    const found = fib4Problem(measurements)
    if (found !== null) {
      setResult(null)
      setProblem(found)
      track.calculatorResult({
        calculator: "fib4",
        outcome: "rejected",
        reason: found,
      })
      return
    }
    const score = roundScore(fib4Score(measurements))
    setProblem(null)
    track.calculatorResult({ calculator: "fib4", outcome: "result" })
    setResult({
      score,
      band: fib4BandFor(measurements.age, score),
      ageGroup: fib4AgeGroupFor(measurements.age),
      reducedAccuracy: fib4HasReducedAccuracy(measurements.age),
    })
  }

  function reset() {
    setAge("")
    setAst("")
    setAlt("")
    setPlatelets("")
    invalidate()
  }

  const styles = result ? BAND_STYLES[result.band] : null

  /**
   * Segment widths on the 0-to-5 axis, in score points, taken from the
   * reader's own age group: the lower cut-off is 1.30 under 65 and 2.00 from
   * 65, so a fixed set of boundaries would mislabel the bar under every older
   * reader's result.
   */
  const segments = result
    ? [
        // no-raw-ids: band lookup key, never rendered
        { id: "low" as const, span: result.ageGroup.lowBelow },
        {
          // no-raw-ids: band lookup key, never rendered
          id: "indeterminate" as const,
          span: result.ageGroup.highAbove - result.ageGroup.lowBelow,
        },
        {
          // no-raw-ids: band lookup key, never rendered
          id: "high" as const,
          span: SCALE_MAX - result.ageGroup.highAbove,
        },
      ]
    : []

  const markerPercent = result
    ? Math.min(100, Math.max(0, (result.score / SCALE_MAX) * 100))
    : 0

  const problemMessage = problem
    ? problem === "ageOutOfRange"
      ? t(k.calcAgeOutOfRange)
      : problem === "plateletsUnit"
        ? t(k.calcPlateletsUnit)
        : t(k.calcOutOfRange)
    : null

  return (
    <div
      data-testid="fib4-calculator"
      data-hydrated={hydrated ? "true" : undefined}
      /* One centred column at the prose measure, lining up with the
         sections below it. The result no longer needs a column of its
         own: it does not exist until Calculate has run. */
      className="mx-auto w-full max-w-[760px]"
    >
      {/* ── Intro ── */}
      <div className="text-center">{intro}</div>

      {/* ── Form ── */}
      {/*
        `noValidate` is load-bearing, not a style choice.

        The number inputs below carry `min`/`max` so a phone shows a sensible
        keypad and a stepper cannot walk outside the accepted range. But those
        attributes also arm the browser's own constraint validation, which
        runs BEFORE `onSubmit` and cancels the submit outright on a violation.
        On the SCORE2 page that made the age field look like a dead button:
        `handleSubmit` never ran, `problem` was never set, and the result
        panel sat on its empty-state line. A CI Playwright run caught it.

        It also has to be `noValidate` rather than dropping `min`/`max`,
        because the browser's bubble cannot say what these messages say.
        "Value must be less than or equal to 1000" is the wrong thing to tell
        someone who entered their platelet count per microlitre: the number is
        not too large, it is in the wrong unit, and that is the one mistake on
        this page that would otherwise divide a score by a thousand.
      */}
      <form onSubmit={handleSubmit} noValidate className="mt-10">
        <h2 className="type-title-lg text-center text-neutral-900">
          {t(k.calcTitle)}
        </h2>

        <div className="mt-6 space-y-4">
          <div>
            <label htmlFor={`${fieldId}-age`} className={labelClasses}>
              {t(k.calcAgeLabel)}
            </label>
            <div className="relative mt-2">
              <input
                id={`${fieldId}-age`}
                data-testid="fib4-age"
                type="number"
                inputMode="numeric"
                min={FIB4_MIN_AGE}
                max={FIB4_MAX_AGE}
                step="1"
                autoComplete="off"
                placeholder="55"
                value={age}
                onChange={(e) => {
                  setAge(e.target.value)
                  invalidate()
                }}
                className={cn(inputClasses, "pr-20")}
              />
              <span className={unitSuffixClasses}>{t(k.calcUnitYears)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor={`${fieldId}-ast`} className={labelClasses}>
                {t(k.calcAstLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-ast`}
                  data-testid="fib4-ast"
                  type="number"
                  inputMode="decimal"
                  min={MIN_AST}
                  max={MAX_AST}
                  step="1"
                  autoComplete="off"
                  placeholder="30"
                  value={ast}
                  onChange={(e) => {
                    setAst(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-16")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitUl)}</span>
              </div>
            </div>
            <div>
              <label htmlFor={`${fieldId}-alt`} className={labelClasses}>
                {t(k.calcAltLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-alt`}
                  data-testid="fib4-alt"
                  type="number"
                  inputMode="decimal"
                  min={MIN_ALT}
                  max={MAX_ALT}
                  step="1"
                  autoComplete="off"
                  placeholder="35"
                  value={alt}
                  onChange={(e) => {
                    setAlt(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-16")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitUl)}</span>
              </div>
            </div>
          </div>
          <p className={hintClasses}>{t(k.calcEnzymeHint)}</p>

          <div>
            <label htmlFor={`${fieldId}-platelets`} className={labelClasses}>
              {t(k.calcPlateletsLabel)}
            </label>
            <div className="relative mt-2">
              <input
                id={`${fieldId}-platelets`}
                data-testid="fib4-platelets"
                type="number"
                inputMode="decimal"
                min={MIN_PLATELETS}
                max={MAX_PLATELETS}
                step="1"
                autoComplete="off"
                placeholder="250"
                value={platelets}
                onChange={(e) => {
                  setPlatelets(e.target.value)
                  invalidate()
                }}
                className={cn(inputClasses, "pr-16")}
              />
              <span className={unitSuffixClasses}>{t(k.calcUnitGl)}</span>
            </div>
          </div>
          <p className={hintClasses}>{t(k.calcPlateletsHint)}</p>
        </div>

        <div className="mt-6 flex items-center gap-4">
          {/*
            `buttonVariants` on a bare <button> rather than <Button>: the
            idiom the other calculators use, and it keeps `data-testid` off a
            custom component's prop type.
          */}
          <button
            type="submit"
            data-testid="fib4-submit"
            disabled={measurements === null}
            className={cn(buttonVariants({ variant: "primary" }), "flex-1")}
          >
            {t(k.calcSubmit)}
          </button>
          {hasInput && (
            <button
              type="button"
              onClick={reset}
              className="type-title-sm shrink-0 cursor-pointer text-neutral-500 underline underline-offset-4 transition-colors hover:text-neutral-900"
            >
              {t(k.calcReset)}
            </button>
          )}
        </div>

        {/* Who the score applies to, next to the button rather than buried
            further down the page: a reader in the middle of an acute
            hepatitis needs to know before they read a number, not after. */}
        <p className={hintClasses}>{t(k.calcEligibilityNote)}</p>

        {/*
          The refusal message used to be the result panel's status line. With
          that panel gone until Calculate succeeds, it belongs beside the
          control that produced it. `data-state` still names WHICH refusal is
          showing, so a test can tell them apart without asserting copy in
          three languages — and there is no longer an "empty" value for a
          green-looking spec to pass on by accident.
        */}
        {problemMessage !== null && (
          <p
            data-testid="fib4-status"
            data-state={problem}
            className="type-body-md text-terracotta-700 mt-4 text-center"
          >
            {problemMessage}
          </p>
        )}
      </form>

      {/* ── Result ── */}
      {/*
        The live region is mounted from the start so the panel is
        announced when it arrives; the panel itself only exists once
        Calculate has produced a figure, which is the point of the
        layout.
      */}
      <div aria-live="polite">
        {result !== null && styles !== null && (
          <div
            data-testid="fib4-result"
            className="mt-8 rounded-[16px] bg-white p-6 md:p-8"
          >
            <div>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="type-label-md text-neutral-500 normal-case">
                    {t(k.calcResultLabel)}
                  </p>
                  {/* No unit after the number: FIB-4 is an index, and the two
                      decimals are how the literature and every clinical
                      calculator print it. */}
                  <p
                    data-testid="fib4-value"
                    className={cn(
                      "type-display-md mt-1 tabular-nums",
                      styles.accent
                    )}
                  >
                    {formatDecimal(result.score, 2, locale)}
                  </p>
                </div>
                {/*
                  `data-band` carries the band ID beside the translated label.
                  The three labels share no substring across en/de/fr ("Low",
                  "Tief", "Bas"), so a text regex cannot check the band in every
                  locale the page serves, and asserting the English wording would
                  red the whole suite on a translation pass.
                */}
                <span
                  data-testid="fib4-band"
                  data-band={result.band}
                  className={cn(
                    "type-title-sm rounded-full px-4 py-1.5",
                    styles.chip
                  )}
                >
                  {t(FIB4_BANDS[result.band].labelKey)}
                </span>
              </div>

              {/* Scale */}
              <div className="mt-6">
                {/* The age group rides in parentheses rather than being glued
                    onto the end of the label: the group labels are noun phrases
                    ("under 65", "moins de 65 ans") that read as a fragment after
                    a preposition in French and German, and a second key per group
                    just to carry the preposition would be more strings to keep in
                    step with the table's row headers. */}
                <p className="type-label-md text-neutral-500 normal-case">
                  {t(k.calcThresholdsLabel)} ({t(result.ageGroup.labelKey)})
                </p>
                <div className="relative mt-3">
                  {/*
                    Contiguous, no gap between segments. A gap would push every
                    boundary right of its true position while the marker is
                    placed on the ungapped score, so the marker would stop landing
                    on the band it names. Rounding lives on the clipping container
                    instead of the individual segments.
                  */}
                  <div className="flex h-2 w-full overflow-hidden rounded-full">
                    {segments.map((segment) => (
                      <div
                        key={segment.id}
                        className={cn("h-full", BAND_STYLES[segment.id].bar)}
                        style={{
                          width: `${(segment.span / SCALE_MAX) * 100}%`,
                        }}
                      />
                    ))}
                  </div>
                  <div
                    aria-hidden
                    className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-neutral-900 bg-white transition-[left] duration-300 ease-out"
                    style={{ left: `${markerPercent}%` }}
                  />
                </div>
                {/*
                  Ticks are absolutely positioned at their real place on the axis.
                  The two cut-offs are not evenly spaced (1.30 sits at 26% of the
                  0-to-5 axis and 2.67 at 53%), so a flex justify-between row
                  would print them at even intervals and mislabel the bands
                  underneath.
                */}
                <div aria-hidden className="relative mt-3 h-4">
                  <span className="type-label-sm absolute top-0 left-0 text-neutral-400 normal-case tabular-nums">
                    {formatDecimal(0, 2, locale)}
                  </span>
                  {[result.ageGroup.lowBelow, result.ageGroup.highAbove].map(
                    (value) => (
                      <span
                        key={value}
                        className="type-label-sm absolute top-0 -translate-x-1/2 text-neutral-400 normal-case tabular-nums"
                        style={{ left: `${(value / SCALE_MAX) * 100}%` }}
                      >
                        {formatDecimal(value, 2, locale)}
                      </span>
                    )
                  )}
                  <span className="type-label-sm absolute top-0 right-0 text-neutral-400 normal-case tabular-nums">
                    {formatDecimal(SCALE_MAX, 2, locale)}+
                  </span>
                </div>
                <p className="type-body-sm mt-2 text-neutral-500">
                  {t(k.calcScaleCaption)}
                </p>
              </div>

              <p
                data-testid="fib4-meaning"
                className="type-body-md mt-6 border-t border-neutral-200 pt-5 text-neutral-600"
              >
                {t(FIB4_BANDS[result.band].meaningKey)}
              </p>

              {/* Shown only under 35, where a low score is close to a foregone
                  conclusion. A caveat and not a refusal: the score IS the
                  published score at 30, and what a 30-year-old needs is to know
                  how little a low one proves. */}
              {result.reducedAccuracy && (
                <p
                  data-testid="fib4-young-age-note"
                  className="type-body-sm mt-4 text-neutral-500"
                >
                  {t(k.calcYoungAgeNote)}
                </p>
              )}
            </div>
            {/*
              Check-up hand-off. Inside the result panel and gated on `result`, so
              it is the reward for pressing Calculate rather than another CTA
              sitting on the page from the start. It books the market's blood
              check-up directly (CalculatorBookingCta) rather than sending the
              reader through the quiz first, and it does NOT claim the booking
              reads this result.
            */}
            <div className="bg-aubergine-50 mt-6 rounded-lg p-5">
              <h3 className="type-title-md text-neutral-900">
                {t(k.quizHeading)}
              </h3>
              <p className="type-body-md mt-2 text-neutral-600">
                {t(k.quizBody)}
              </p>
              <CalculatorBookingCta calculator="fib4">
                {t(k.quizCta)}
              </CalculatorBookingCta>
            </div>
          </div>
        )}
      </div>

      {/* The caveat is about the tool, not the answer, so it stays on
          the page whether or not anything has been calculated. */}
      <p className="type-body-sm mx-auto mt-8 max-w-[600px] text-center text-neutral-500">
        {t(k.calcDisclaimer)}
      </p>
    </div>
  )
}
